import {
  AccountApiError,
  type AccountApiErrorContext,
  isRecord,
  requestAccountJson
} from "./account-api";

export type AlertRuleType = "watchlist_filing" | "ceo_cfo_buy" | "cluster_buy";

export interface AlertRule {
  id: string;
  name: string;
  type: AlertRuleType;
  params: {
    minValue?: number | null;
    tickers?: string[];
    name?: string;
  };
  channel: "email";
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AlertDelivery {
  id: string;
  ruleId: string;
  userKey: string;
  eventKey: string;
  channel: string;
  status: string;
  error: string | null;
  sentAt: string | null;
  createdAt: string;
  ruleType?: string | null;
  ruleName?: string | null;
}

export class AlertsApiError extends AccountApiError {
  constructor(code: string, message: string, context: AccountApiErrorContext) {
    super(code, message, context);
    this.name = "AlertsApiError";
  }
}

const createError = (code: string, message: string, context: AccountApiErrorContext) =>
  new AlertsApiError(code, message, context);

function parseRule(value: unknown, endpoint: string): AlertRule {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    typeof value.name !== "string" ||
    typeof value.type !== "string" ||
    typeof value.isEnabled !== "boolean" ||
    typeof value.createdAt !== "string" ||
    typeof value.updatedAt !== "string"
  ) {
    throw new AlertsApiError("alerts_invalid", "Alert rule response has an invalid shape", {
      endpoint
    });
  }
  return {
    id: value.id,
    name: value.name,
    type: value.type as AlertRuleType,
    params: isRecord(value.params) ? (value.params as AlertRule["params"]) : {},
    channel: "email",
    isEnabled: value.isEnabled,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt
  };
}

function parseDelivery(value: unknown, endpoint: string): AlertDelivery {
  if (
    !isRecord(value) ||
    typeof value.id !== "string" ||
    typeof value.ruleId !== "string" ||
    typeof value.status !== "string" ||
    typeof value.createdAt !== "string"
  ) {
    throw new AlertsApiError("alerts_invalid", "Alert delivery response has an invalid shape", {
      endpoint
    });
  }
  return {
    id: value.id,
    ruleId: value.ruleId,
    userKey: typeof value.userKey === "string" ? value.userKey : "",
    eventKey: typeof value.eventKey === "string" ? value.eventKey : "",
    channel: typeof value.channel === "string" ? value.channel : "email",
    status: value.status,
    error: typeof value.error === "string" ? value.error : null,
    sentAt: typeof value.sentAt === "string" ? value.sentAt : null,
    createdAt: value.createdAt,
    ruleType: typeof value.ruleType === "string" ? value.ruleType : null,
    ruleName: typeof value.ruleName === "string" ? value.ruleName : null
  };
}

export async function loadAlertRules(): Promise<AlertRule[]> {
  const endpoint = "/api/alerts";
  const payload = await requestAccountJson({
    endpoint,
    unavailableCode: "alerts_unavailable",
    httpCode: "alerts_http",
    invalidCode: "alerts_invalid",
    createError
  });
  if (!isRecord(payload) || !Array.isArray(payload.rules)) {
    throw new AlertsApiError("alerts_invalid", "Alerts response has an invalid shape", { endpoint });
  }
  return payload.rules.map((item) => parseRule(item, endpoint));
}

export async function createAlertRule(input: {
  name: string;
  type: AlertRuleType;
  params?: AlertRule["params"];
  isEnabled?: boolean;
}): Promise<AlertRule> {
  const endpoint = "/api/alerts";
  const payload = await requestAccountJson({
    endpoint,
    init: {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: input.name,
        type: input.type,
        params: input.params ?? {},
        isEnabled: input.isEnabled ?? true
      })
    },
    unavailableCode: "alerts_unavailable",
    httpCode: "alerts_http",
    invalidCode: "alerts_invalid",
    createError
  });
  if (!isRecord(payload)) {
    throw new AlertsApiError("alerts_invalid", "Create alert response invalid", { endpoint });
  }
  return parseRule(payload.rule, endpoint);
}

export async function createTickerFilingAlert(ticker: string): Promise<AlertRule> {
  const normalized = ticker.trim().toUpperCase();
  if (!normalized) {
    throw new AlertsApiError("alerts_invalid", "Ticker required", { endpoint: "/api/alerts" });
  }
  return createAlertRule({
    name: `${normalized} filings`,
    type: "watchlist_filing",
    params: { tickers: [normalized] },
    isEnabled: true
  });
}

export async function deleteAlertRule(id: string): Promise<void> {
  const endpoint = `/api/alerts/${encodeURIComponent(id)}`;
  await requestAccountJson({
    endpoint,
    init: { method: "DELETE" },
    unavailableCode: "alerts_unavailable",
    httpCode: "alerts_http",
    invalidCode: "alerts_invalid",
    createError
  });
}

export async function loadAlertDeliveries(limit = 50): Promise<AlertDelivery[]> {
  const endpoint = `/api/alerts/deliveries?limit=${encodeURIComponent(String(limit))}`;
  const payload = await requestAccountJson({
    endpoint,
    unavailableCode: "alerts_unavailable",
    httpCode: "alerts_http",
    invalidCode: "alerts_invalid",
    createError
  });
  if (!isRecord(payload) || !Array.isArray(payload.deliveries)) {
    throw new AlertsApiError("alerts_invalid", "Deliveries response has an invalid shape", {
      endpoint
    });
  }
  return payload.deliveries.map((item) => parseDelivery(item, endpoint));
}
